import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { emitVisitCompleteWhenPhotosSettle, emitVisitError } from '../lib/embedBridge';
import { useSearchParams } from 'react-router-dom';
import ProgressBar from './intake/ProgressBar';
import QuestionStep from './intake/QuestionStep';
import MultiSelectStep from './intake/MultiSelectStep';
import MultiSelectWithAddonsStep from './intake/MultiSelectWithAddonsStep';
import TextareaStep from './intake/TextareaStep';
import TextareaWithUploadStep from './intake/TextareaWithUploadStep';
import HeightWeightBmiStep from './intake/HeightWeightBmiStep';
import ConsentDropdownStep from './intake/ConsentDropdownStep';
import InputStep from './intake/InputStep';
import DisqualifiedStep from './intake/DisqualifiedStep';
import Footer from './intake/Footer';
import FollowUpSuccessStep from './followup/FollowUpSuccessStep';
import FollowUpEmailStep from './followup/FollowUpEmailStep';
import PrefilledTextareaStep from './followup/PrefilledTextareaStep';
import PrefilledQuestionStep from './followup/PrefilledQuestionStep';
import InfoTextStep from './followup/InfoTextStep';
import ShipToCard from './followup/ShipToCard';
import useFollowupShipTo from './followup/useFollowupShipTo';
import useFollowupQuote from './followup/useFollowupQuote';
import RefillPharmacyStep from './followup/RefillPharmacyStep';
import RefillDoseStep from './followup/RefillDoseStep';
import RefillQuoteCheckout from './followup/RefillQuoteCheckout';
import VideoVisitNote from './intake/VideoVisitNote';
import {
  REFILL_DOSE_ID, REFILL_PHARMACY_ID, REFILL_SLIDE_IDS,
  readRefillParams, wantsQuote, goalTypeFromQuote, insertRefillSteps, needsDoseStep, routeForPharmacyAnswer, defaultPharmacyAnswer,
  doseRequestQa, submitExtrasFrom, visitCompleteExtrasFrom, isQuoteAlreadySubmittedError,
} from '../lib/followupQuote';
import { shipToFromLookup } from '../lib/followupShipTo';
import MedicationSelectionStep from './intake/MedicationSelectionStep';
import CheckoutStep from './intake/CheckoutStep';
import BrandLogo from '@/components/BrandLogo';
import { followUpQuestions, buildDynamicFollowUpFlow, getQuestionById, isOnStarterDose, getStarterDoseForMedication } from '../data/followUpData';
import {
  buildDynamicMetabolicFollowUpFlow,
  getMetabolicQuestionById,
  detectGoalType,
} from '../data/metabolicFollowUpData';
import {
  buildDynamicAntiAgingFollowUpFlow,
  getAntiAgingQuestionById,
} from '../data/antiAgingFollowUpData';
import {
  buildDynamicTrtFollowUpFlow,
  getTrtFollowUpQuestionById,
  computeTrtFollowUpQadam,
} from '../data/trtFollowUpData';
import { isTrtBlockedState, TRT_BLOCKED_STATE_MESSAGE } from '../data/trtData';
import {
  buildDynamicWomenSexualHealthFollowUpFlow,
  getWomenSexualHealthFollowUpQuestionById,
} from '../data/womenSexualHealthFollowUpData';
import {
  buildDynamicMensSexualHealthFollowUpFlow,
  getMensSexualHealthFollowUpQuestionById,
} from '../data/mensSexualHealthFollowUpData';
import {
  buildDynamicBrandedWeightlossFollowUpFlow,
  getBrandedWeightlossFollowUpQuestionById,
} from '../data/brandedWeightlossFollowUpData';
import { apiService } from '../services/api';
import { consentAnswerLabel, composeQaQuestionText, describeSubmitFailure, formatAddonsAnswer, formatMultiSelectAnswer, splitHeightWeightAnswer, computeAgeFromDob, GH_THERAPY_MIN_AGE } from '../lib/utils';
import { MutatingDots } from 'react-loader-spinner';

// The Beluga visitType a follow-up submits for its goalType. Also keys the
// §video-visit lookup at checkout, so the two can never disagree.
const followupVisitType = (goalType) =>
  goalType === 'antiAging'
    ? 'antiAgingFollowup'
    : goalType === 'metabolic'
    ? 'metabolicSupportFollowup'
    : goalType === 'trt'
    ? 'trtFollowup'
    : goalType === 'womenSexualHealth'
    ? 'womenSexualHealthFollowup'
    : goalType === 'mensSexualHealth'
    ? 'mensSexualHealthFollowup'
    : 'weightlossfollowup';

const FollowUpForm = () => {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email');
  const masterIdParam = searchParams.get('masterId');
  const phoneParam = searchParams.get('phone');
  const isEmbed = searchParams.get('embed') === '1';
  // Smoke-test escape hatch — production embed callers should rely on
  // userData.primaryGoal / patientPreference auto-detection instead.
  const typeParam = searchParams.get('type');
  // §branded-refill: the dashboard's refill launch names the prescription
  // (rxId / medId, plus hints). Only then do we ask the backend for a quote.
  const refillParams = useMemo(() => readRefillParams((k) => searchParams.get(k)), [searchParams]);

  // User lookup state
  const [userEmail, setUserEmail] = useState(emailParam || '');
  const [userData, setUserData] = useState(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [isUserVerified, setIsUserVerified] = useState(false);
  
  // Form state
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [visitResult, setVisitResult] = useState(null); // §video-visit note
  const [slideDirection, setSlideDirection] = useState('initial');
  const [slideKey, setSlideKey] = useState(0);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [loading, setLoading] = useState(false);

  // Single-select auto-advance gating (§FE_PRD_SINGLE_SELECT_AUTOADVANCE_RACE).
  // advancedForSlideKeyRef ensures we schedule at most ONE advance per slideKey,
  // so a user who changes their selection on the same slide cannot stack timers
  // and accidentally skip the next slide. advanceTimerRef holds the pending
  // timer id so it can be cancelled on unmount.
  const advancedForSlideKeyRef = useRef(null);
  const advanceTimerRef = useRef(null);
  useEffect(() => () => {
    if (advanceTimerRef.current !== null) clearTimeout(advanceTimerRef.current);
  }, []);

  // EMS warning modal — TRT followup acute-symptoms=Yes pops this. Not a
  // disqualifier; provider review still happens. Modal carries the verbatim
  // EMS message from the option's `emsWarningMessage` field. Two CTAs:
  // Call 911 (tel:911) and Continue with my visit anyway. See
  // BACKEND_TRT_FOLLOWUP_PRD §5.1 and Emergent dashboard PRD §3.1.
  const [emsWarning, setEmsWarning] = useState(null);
  
  // §branded-refill: GET /followup/quote once the patient is verified (we hold
  // the prior masterId from then on). Any dashboard launch (embed + masterId)
  // asks — the Rx's medId can be null on the dashboard side (live issue,
  // patient 141, 2026-10-07) and the BE resolves it. quote === null → today's flow.
  const refillQuote = useFollowupQuote({
    enabled: isUserVerified && wantsQuote(refillParams, { embed: isEmbed, masterId: userData?.masterId || masterIdParam || null }),
    masterId: userData?.masterId || masterIdParam || null,
    rxId: refillParams.rxId,
    rxRowId: refillParams.rxRowId,
    medId: refillParams.medId,
    fulfillmentRoute: refillParams.fulfillmentRoute,
  });
  const quote = refillQuote.quote;
  // EVERY quoted refill is bound to its quote (BE #341): the checkout shows
  // the QUOTED medication (consult-only on the local leg, the medication price
  // on the partner leg) and pays with selectedMedIds:[quote.medId] + quoteId +
  // quote.visitType. The plan picker / planId path only renders with no quote.
  // (Before #341 the medication leg was deliberately unbound: the plan picker
  // paid via planId, which the quote gate rejects — that is the bug the owner
  // hit on a TRT refill: "Semaglutide — 1 Month Supply $199".)
  const quoteBound = Boolean(quote && quote.quoteId);

  // Goal type drives which questionnaire is rendered (GLP-1 weightloss vs.
  // metabolic Sermorelin/Tesamorelin). Auto-detected from the patient's
  // existing patientPreference; overridable via ?type= for smoke testing.
  // §branded-refill: the quote's followupIntakeType wins over both — a
  // branded-tablet Rx whose patientPreference / ?type= said compounded
  // weightloss must run the BRANDED questionnaire and submit
  // flow 'followup_brandedWeightloss' (the BE forces the flow; the questions
  // and the plan picker are ours).
  const goalType = useMemo(
    () => goalTypeFromQuote(quote) || detectGoalType(userData, typeParam),
    [quote, userData, typeParam]
  );

  // Build dynamic flow based on current answers
  const currentFlow = useMemo(() => {
    if (!isUserVerified) return [];
    const withRefill = (flow) => insertRefillSteps(flow, quote);
    if (goalType === 'antiAging') {
      return withRefill(buildDynamicAntiAgingFollowUpFlow(answers, userData));
    }
    if (goalType === 'metabolic') {
      return withRefill(buildDynamicMetabolicFollowUpFlow(answers, userData));
    }
    if (goalType === 'trt') {
      return withRefill(buildDynamicTrtFollowUpFlow(answers, userData));
    }
    if (goalType === 'womenSexualHealth') {
      return withRefill(buildDynamicWomenSexualHealthFollowUpFlow(answers, userData));
    }
    if (goalType === 'mensSexualHealth') {
      return withRefill(buildDynamicMensSexualHealthFollowUpFlow(answers, userData));
    }
    if (goalType === 'brandedWeightloss') {
      return withRefill(buildDynamicBrandedWeightlossFollowUpFlow(answers, userData));
    }
    return withRefill(buildDynamicFollowUpFlow(answers, userData));
  }, [answers, userData, isUserVerified, goalType, quote]);
  
  const totalSteps = currentFlow.length;
  const currentQuestion = currentFlow[currentStep];
  // §patient-address: Ship-to card state. Asks the dashboard for a session token
  // only once the checkout step is reached (see useFollowupShipTo).
  const shipTo = useFollowupShipTo({
    userData,
    previousMasterId: userData?.masterId || masterIdParam || null,
    active: isUserVerified && currentQuestion?.type === 'checkout',
  });
  
  // Pre-populate answers from user data when verified
  useEffect(() => {
    if (userData && isUserVerified) {
      const prefilled = {};
      
      // Map user data to answer fields
      if (userData.medicalConditions) {
        prefilled['medical-conditions'] = userData.medicalConditions;
      }
      if (userData.selfReportedMeds) {
        prefilled['self-reported-meds'] = userData.selfReportedMeds;
      }
      if (userData.allergies) {
        prefilled['allergies'] = userData.allergies;
      }
      // Sex-specific visit types must reflect the visit type, not a possibly-
      // wrong inherited/looked-up value. The backend maps this `sex-at-birth`
      // answer → the Beluga `sex` field (named-field mapping), so a stale Female
      // here mislabels a men's chart even when contact.sex is Male (Beluga
      // flagged mensSexualHealthFollowup 67631419/81565127, 2026-06-05).
      // mensSexualHealth/trt → male; womenSexualHealth → female; else inherit.
      const sexForFlow =
        (goalType === 'mensSexualHealth' || goalType === 'trt')
          ? 'male'
          : goalType === 'womenSexualHealth'
          ? 'female'
          : userData.sex;
      if (sexForFlow) {
        prefilled['sex-at-birth'] = sexForFlow;
      }

      // Height/weight live in previousAnswers.formObj as split records
      // (Q0/A0 = "What is your height in feet and inches?" → "5'6\"",
      //  Q1/A1 = "What is your weight in pounds?" → "155"). Parse them
      // back into the {feet, inches, weight} shape HeightWeightBmiStep
      // and splitHeightWeightAnswer both expect. Without this prefill,
      // metabolicSupportFollowup smokes trip FE_PAYLOAD_INCOMPLETE on
      // height-weight (see FE_PRD_FOLLOWUP_HEIGHT_WEIGHT_SLIDE_ID.md).
      const formObj = userData.previousAnswers?.formObj;
      if (formObj) {
        let feet, inches, weight;
        for (let i = 0; i < 40; i++) {
          const q = formObj[`Q${i}`];
          const a = formObj[`A${i}`];
          if (q === undefined) break;
          if (/height in feet and inches/i.test(q)) {
            const m = /(\d+)'(\d+)"/.exec(String(a));
            if (m) { feet = m[1]; inches = m[2]; }
          } else if (/weight in pounds/i.test(q)) {
            const m = /(\d+(?:\.\d+)?)/.exec(String(a));
            if (m) weight = m[1];
          }
        }
        if (feet && weight) {
          prefilled['height-weight'] = { feet, inches: inches || '0', weight };
        }
      }

      setAnswers(prev => ({ ...prev, ...prefilled }));
    }
  }, [userData, isUserVerified, goalType]);
  
  // Embed-mode bootstrap: when ?embed=1 + any identity (masterId/email/phone)
  // is in the URL, auto-resolve the user and skip the lookup gate. Direct-
  // traffic visitors still see the gate (those callers won't have embed=1).
  // Backend lookup resolver priority is masterId > email > phone.
  useEffect(() => {
    if (isUserVerified) return;
    if (isEmbed && (masterIdParam || emailParam || phoneParam)) {
      handleUserLookup({ masterId: masterIdParam, email: emailParam, phone: phoneParam });
      return;
    }
    if (emailParam) {
      // Legacy direct-traffic ?email= deep link (will be removed by §0.A
      // route guard once shipped, but kept here so the guard stays the
      // single source of truth).
      handleUserLookup({ email: emailParam });
    }
    // handleUserLookup is intentionally omitted — it's a stable inline fn
    // declared further down, and including it would re-fire the lookup
    // every render. isUserVerified is the gate that prevents re-lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailParam, masterIdParam, phoneParam, isEmbed, isUserVerified]);

  // §0.A — Direct traffic to /followup is no longer a self-serve surface.
  // The patient dashboard owns all follow-up entry points; this route only
  // renders for embed callers. Non-embed hits redirect to dashboard login
  // (preserving any UTM params for marketing attribution). Implemented as
  // a useEffect so it runs after hooks have been declared in stable order.
  useEffect(() => {
    if (isEmbed || typeof window === 'undefined') return;
    const utm = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
      .map((k) => (searchParams.get(k) ? `${k}=${encodeURIComponent(searchParams.get(k))}` : null))
      .filter(Boolean)
      .join('&');
    const next = '/patient-view/medications';
    const dashUrl = `https://fitpal.com/login?next=${encodeURIComponent(next)}${utm ? '&' + utm : ''}`;
    window.location.replace(dashUrl);
  }, [isEmbed, searchParams]);

  // Look up user by masterId, email, or phone. Accepts an object form for
  // embed callers; legacy (email, phone) signature still works for the
  // FollowUpEmailStep gate.
  const handleUserLookup = async (idOrEmail, legacyPhone) => {
    setIsLookingUp(true);
    setLookupError('');

    try {
      const response = await apiService.lookupFollowUpUser(idOrEmail, legacyPhone);
      
      if (response.success && response.user) {
        // Carry previousAnswers alongside user so the prefill useEffect can
        // hydrate slides whose values live there (e.g., height/weight, which
        // backend stores in previousAnswers.formObj.A0/A1 rather than as
        // top-level user fields).
        setUserData({ ...response.user, previousAnswers: response.previousAnswers });
        setUserEmail(response.user.email || email);
        setIsUserVerified(true);
        setSlideDirection('forward');
        setSlideKey(prev => prev + 1);
      } else if (response.needsPhone) {
        // User exists but we need phone number to fetch their data
        setLookupError(response.message || 'Account found! Please use your phone number to look up your records.');
      } else {
        setLookupError(response.message || 'User not found. Please check your email or phone number.');
      }
    } catch (error) {
      console.error('User lookup error:', error);
      setLookupError('An error occurred while looking up your information. Please try again.');
    } finally {
      setIsLookingUp(false);
    }
  };
  
  // Handle disqualification
  const handleDisqualify = useCallback((reason) => {
    setIsDisqualified(true);
    setDisqualifyReason(reason);
    setSlideDirection('forward');
    setSlideKey(prev => prev + 1);
  }, []);

  // TRT state gate — Beluga discontinued TRT in AL/AR/CT/ND/RI (2026-07-20).
  // Follow-ups collect no shipping address; the patient's state rides the
  // lookup payload from the parent visit. No state on the payload → let the
  // visit through and rely on the BE/provider gate.
  useEffect(() => {
    if (!isUserVerified || goalType !== 'trt' || isDisqualified) return;
    if (isTrtBlockedState(userData?.state)) {
      handleDisqualify(TRT_BLOCKED_STATE_MESSAGE);
    }
  }, [isUserVerified, goalType, userData, isDisqualified, handleDisqualify]);
  
  // Handle answer
  const handleAnswer = useCallback((questionId, answer) => {
    const previousAnswer = answers[questionId];

    // Set the new answer
    const newAnswers = {
      ...answers,
      [questionId]: answer
    };
    setAnswers(newAnswers);

    // Goal-aware lookups — without these, flow-specific questions
    // (current-medication-status, medication-progress, etc.) never resolve
    // a question record and skip the disqualifier + auto-advance branches.
    const lookup =
      goalType === 'antiAging'
        ? getAntiAgingQuestionById
        : goalType === 'metabolic'
        ? getMetabolicQuestionById
        : goalType === 'trt'
        ? getTrtFollowUpQuestionById
        : goalType === 'womenSexualHealth'
        ? getWomenSexualHealthFollowUpQuestionById
        : goalType === 'mensSexualHealth'
        ? getMensSexualHealthFollowUpQuestionById
        : goalType === 'brandedWeightloss'
        ? getBrandedWeightlossFollowUpQuestionById
        : getQuestionById;
    const flowBuilder =
      goalType === 'antiAging'
        ? buildDynamicAntiAgingFollowUpFlow
        : goalType === 'metabolic'
        ? buildDynamicMetabolicFollowUpFlow
        : goalType === 'trt'
        ? buildDynamicTrtFollowUpFlow
        : goalType === 'womenSexualHealth'
        ? buildDynamicWomenSexualHealthFollowUpFlow
        : goalType === 'mensSexualHealth'
        ? buildDynamicMensSexualHealthFollowUpFlow
        : goalType === 'brandedWeightloss'
        ? buildDynamicBrandedWeightlossFollowUpFlow
        : buildDynamicFollowUpFlow;

    // Check for disqualifying answers. Per-option `disqualifyReason` (added
    // for TRT followup acute-symptoms — see docs/BACKEND_TRT_FOLLOWUP_PRD.md
    // §5.1) lets the slide ship its own copy. Fallback is the generic decline
    // message used by all other flows.
    const question = lookup(questionId);
    let selectedOption = null;
    if (question?.options) {
      selectedOption = question.options.find(opt =>
        (opt.value || opt) === answer
      );
      if (selectedOption?.disqualifies) {
        const reason =
          selectedOption.disqualifyReason ||
          'You have indicated that you do not wish to continue with this treatment.';
        handleDisqualify(reason);
        return;
      }
    }

    // EMS warning interception for TRT followup acute-symptoms=Yes. Per the
    // canonical BE PRD (2026-05-25), this is NOT a hard disqualifier — we
    // surface the warning modal then let the patient continue when they're
    // ready. Suppress auto-advance; the modal's Continue handler advances.
    if (selectedOption?.displayEmsWarning) {
      setEmsWarning({
        message:
          selectedOption.emsWarningMessage ||
          'These are concerning symptoms that should prompt immediate medical evaluation. Please contact EMS services immediately for further evaluation.',
        slideKey,
      });
      return;
    }

    // Auto-advance for single-select questions — single-fire per slideKey so
    // rapid re-taps on the same slide cannot stack timers and skip the next
    // slide. See FE_PRD_SINGLE_SELECT_AUTOADVANCE_RACE.
    // NOTE: 'single-select-prefilled' is intentionally excluded — that type's
    // step component (PrefilledQuestionStep) owns its own advance and fires
    // on every click (including a "confirm" click that matches the prefilled
    // value). Including it here caused a double-advance that skipped the
    // next slide (height-weight) on W3 weightlossfollowup smoke.
    if (question?.type === 'single-select' && answer !== previousAnswer) {
      if (advancedForSlideKeyRef.current === slideKey) return;
      advancedForSlideKeyRef.current = slideKey;
      if (advanceTimerRef.current !== null) clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = setTimeout(() => {
        advanceTimerRef.current = null;
        // Recalculate flow with new answers to get correct totalSteps
        const newFlow = insertRefillSteps(flowBuilder(newAnswers, userData), quote);
        const newTotalSteps = newFlow.length;

        setCurrentStep(prev => {
          if (prev >= newTotalSteps - 1) return prev;
          setSlideDirection('forward');
          setSlideKey(k => k + 1);
          return prev + 1;
        });
      }, 200);
    }
  }, [answers, userData, handleDisqualify, goalType, slideKey, quote]);
  
  // Handle next — final step is the checkout question, which renders CheckoutStep
  // and dispatches handleCheckoutComplete on successful payment. No Next-triggered
  // submit path exists anymore.
  const handleNext = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setSlideDirection('forward');
      setSlideKey(prev => prev + 1);
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, totalSteps]);

  // EMS modal "Continue with my visit anyway" handler. Closes the modal and
  // advances the form past the acute-symptoms slide. (Call 911 is a tel:
  // anchor, not a JS handler.)
  const handleEmsContinue = useCallback(() => {
    setEmsWarning(null);
    if (currentStep < totalSteps - 1) {
      setSlideDirection('forward');
      setSlideKey(prev => prev + 1);
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, totalSteps]);
  
  // Handle back
  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setSlideDirection('backward');
      setSlideKey(prev => prev + 1);
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);
  
  // §branded-refill: a dose request re-prices the quote (requestedMedId);
  // going back to "keep" re-quotes without it. The hook de-dupes identical
  // requests so a re-render never refetches.
  const requestedMedId = answers[REFILL_DOSE_ID]?.choice === 'change' ? (answers[REFILL_DOSE_ID]?.requestedMedId || null) : null;
  // §manufacturer-pharmacy (BE #345): the pharmacy choice picks a LEG, and the
  // leg is priced by the backend — re-quote with it (manufacturer_pharmacy or
  // local_pharmacy); no answer yet → leave the quote's route alone.
  const pharmacyRoute = routeForPharmacyAnswer(quote, answers[REFILL_PHARMACY_ID]);
  useEffect(() => {
    if (!quote || !pharmacyRoute) return;
    refillQuote.requote({ fulfillmentRoute: pharmacyRoute });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyRoute]);
  // A prior refill sent to the manufacturer defaults the quote to that leg —
  // pre-select the card so the patient confirms rather than re-chooses.
  useEffect(() => {
    if (!quote || answers[REFILL_PHARMACY_ID]) return;
    const d = defaultPharmacyAnswer(quote);
    if (d) setAnswers((prev) => (prev[REFILL_PHARMACY_ID] ? prev : { ...prev, [REFILL_PHARMACY_ID]: d }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.quoteId]);
  const quoteReady = Boolean(quote);
  useEffect(() => {
    if (!quoteReady) return;
    refillQuote.requote({ requestedMedId });
    // refillQuote is a fresh object each render; requote is memoised on its inputs
    // and de-dupes an unchanged request, so this is a no-op until the dose changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedMedId, quoteReady]);

  // Reaching checkout with an expired quote → refresh silently (30-min TTL).
  const atCheckout = isUserVerified && currentQuestion?.type === 'checkout';
  useEffect(() => {
    if (!atCheckout || !quote) return;
    refillQuote.refreshIfExpired();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atCheckout]);

  // 409 quote_stale on the payment call: nothing charged; re-quote and ask the
  // patient to confirm the (possibly changed) amount again.
  const [staleNotice, setStaleNotice] = useState('');
  const handleQuoteStale = useCallback(async () => {
    const fresh = await refillQuote.requote({ force: true });
    setStaleNotice(
      fresh
        ? 'Your price was refreshed — nothing was charged. Please review the amount and confirm again.'
        : 'Your price could not be refreshed — nothing was charged. Please close and reopen this refill from your dashboard.',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refillQuote.requote]);

  // Synchronous re-entry guard. State-based isSubmitting isn't enough —
  // SquarePaymentForm can fire onComplete twice within the same React tick
  // (observed in the 2026-05-04 smoke: duplicate /payments/square-payment-
  // create-payment AND duplicate /followup/submit with identical bodies).
  // A ref updates synchronously and survives the React batching window.
  const submittingRef = useRef(false);

  // Handle checkout complete (after successful payment)
  const handleCheckoutComplete = useCallback(async (paymentResult) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    setLoading(true);

    try {
      // Age-20 gate for growth-hormone-pathway therapies (sermorelin/
      // tesamorelin = metabolic followup) per Beluga policy (Nikka 2026-06-17).
      // DOB comes from the prefilled record / contact. BE re-validates
      // server-side — docs/BACKEND_GH_THERAPY_AGE20_PRD.md.
      if (goalType === 'metabolic') {
        const dob = answers['basic-contact']?.dob || userData?.dob || '';
        const age = computeAgeFromDob(dob);
        if (age !== null && age < GH_THERAPY_MIN_AGE) {
          submittingRef.current = false;
          setIsSubmitting(false);
          setLoading(false);
          handleDisqualify(
            `Sermorelin and tesamorelin are only available to patients age ${GH_THERAPY_MIN_AGE} and older. We're unable to proceed with this request.`,
          );
          return;
        }
      }
      // Resolve patient preference. Metabolic + anti-aging followups never
      // restart at a starter dose (no GLP-1-style escalation pattern) —
      // patient continues their existing patientPreference. Only GLP-1
      // followup keeps the restart-medication branch.
      let patientPreference;
      let restartAtStarterDose = false;
      if (goalType === 'brandedWeightloss') {
        // Branded starter doses (Wegovy/Ozempic/Zepbound/Mounjaro/Foundayo)
        // are resolved backend-side from the parent preference — the FE only
        // signals restart intent. See
        // docs/BACKEND_BRANDED_WEIGHTLOSS_VISIT_PRD.md §4.
        restartAtStarterDose = answers['restart-medication'] === 'consent';
        if (userData?.patientPreference) {
          patientPreference = userData.patientPreference;
        }
      } else if (
        goalType !== 'metabolic' &&
        goalType !== 'antiAging' &&
        goalType !== 'trt' &&
        goalType !== 'womenSexualHealth' &&
        goalType !== 'mensSexualHealth' &&
        answers['restart-medication'] === 'consent' &&
        userData?.patientPreference
      ) {
        const starterDose = getStarterDoseForMedication(userData.patientPreference);
        if (starterDose) {
          patientPreference = [{
            name: starterDose.name,
            strength: starterDose.strength,
            refills: "N/A",
            quantity: "N/A",
            daysSupply: "N/A",
            medId: starterDose.medId,
          }];
          restartAtStarterDose = true;
        }
      } else if (userData?.patientPreference) {
        patientPreference = userData.patientPreference;
      }

      // §branded-refill (BE #341 ask 4): with a quote, the ordered medication is
      // selectedMedIds:[quote.medId]. An explicit patientPreference beats it in
      // CreateVisitV2, and the one from the lookup can be stale (derived from the
      // last payment's plan) — so send none and let the BE build it from the
      // quoted med. The restart-at-starter flag still rides along for the BE.
      if (quote) patientPreference = undefined;

      // Convert answers object → array of {question, answer, slide_id, rawAnswer},
      // matching the shape /beluga/create-visit-v2 expects (PRD §3.1, §3.3).
      // `question` carries the full rendered prompt (with POSSIBLE ANSWERS +
      // consent body) so backend can use it verbatim per
      // BACKEND_QUESTIONNAIRE_TEXT_PARITY_PRD.md §4.1.
      const formattedAnswers = Object.entries(answers).flatMap(([questionId, answer]) => {
        // §branded-refill: the pharmacy pick rides the pharmacySelection /
        // pharmacy envelopes (commerce, not clinical — same ruling as the
        // branded intake). The dose request gets its own Q/A pair below.
        if (REFILL_SLIDE_IDS.has(questionId)) return [];
        const split = splitHeightWeightAnswer(answer);
        if (split) return split;
        const slide = currentFlow.find(q => q.id === questionId);
        // trtFollowup + womenSexualHealthFollowup + mensSexualHealthFollowup are
        // backend bullet flows (BULLET_MULTISELECT_VISITS); send '; ' so comma-
        // containing option labels still split to the right count and bullet.
        // Other followups (antiAging/metabolic/weightloss) keep ', '.
        const labelSep =
          (goalType === 'trt' || goalType === 'womenSexualHealth' || goalType === 'mensSexualHealth' || goalType === 'brandedWeightloss')
            ? '; '
            : ', ';
        const consentLabel = consentAnswerLabel(slide, answer, labelSep);
        let formattedAnswer;
        if (consentLabel !== null) {
          formattedAnswer = consentLabel;
        } else if (slide?.type === 'multi-select-with-addons') {
          // Beluga sees a human-readable bullet list of selected effect
          // labels + their chosen additives. Was leaking JSON via the
          // typeof==='object' fallback before — Erika 2026-05-15 review
          // of WLFU 66402450.
          formattedAnswer = formatAddonsAnswer(answer, slide) ?? '';
        } else if (Array.isArray(answer)) {
          formattedAnswer = formatMultiSelectAnswer(answer);
        } else if (typeof answer === 'object' && answer !== null) {
          if (answer.preview) {
            formattedAnswer = `File: ${answer.fileName || 'uploaded'}`;
          } else if (answer.text !== undefined) {
            formattedAnswer = answer.text + (answer.imageData ? ' [Image attached]' : '');
          } else {
            formattedAnswer = JSON.stringify(answer);
          }
        } else {
          formattedAnswer = String(answer);
        }
        const questionText = composeQaQuestionText(slide) || questionId;
        return [{ question: questionText, answer: formattedAnswer, slide_id: questionId, rawAnswer: answer }];
      });

      // §branded-refill §7: the dose request reaches Beluga's prescriber ONLY
      // through the answers. One Q/A pair, whether or not a change was asked.
      if (quote && needsDoseStep(quote)) {
        formattedAnswers.push(doseRequestQa(quote, answers[REFILL_DOSE_ID]));
      }

      const contactInfo = answers['basic-contact'] || {};
      // §named-field: sex-specific visit types must carry the correct `sex` so a
      // blank/wrong inherited value can't mislabel the chart. Beluga 2026-06-05
      // flagged mensSexualHealthFollowup visits showing Female. mensSexualHealth
      // + trt are assigned-male-at-birth only → Male; womenSexualHealth → Female.
      // Other flows inherit the prefilled sex-at-birth / parent record sex.
      const inheritedSexRaw = answers['sex-at-birth'] || userData?.sex || '';
      const inheritedSex = inheritedSexRaw
        ? inheritedSexRaw.charAt(0).toUpperCase() + inheritedSexRaw.slice(1)
        : '';
      const sex =
        (goalType === 'mensSexualHealth' || goalType === 'trt')
          ? 'Male'
          : goalType === 'womenSexualHealth'
          ? 'Female'
          : inheritedSex;
      const contact = {
        first_name: contactInfo.name?.split(' ')[0] || userData?.firstName || '',
        last_name: contactInfo.name?.split(' ').slice(1).join(' ') || userData?.lastName || '',
        email: contactInfo.email || userEmail || '',
        phone: contactInfo.phone || userData?.phone || '',
        sex,
        // §patient-address: only on the no-token fallback path, and only a
        // patient-confirmed change. It wins over the BE's inheritance and reaches
        // Beluga on this visit. With a token the address was already POSTed.
        ...shipTo.contactFields,
      };
      // Prefer the paymentId from the checkout result (the staging bypass passes
      // the STAGING_TEST_BYPASS sentinel here); fall back to the Square-tokenized
      // id stashed in localStorage for the normal paid flow.
      const paymentId =
        paymentResult?.paymentId ||
        localStorage.getItem('fitpal_intake_payment_id') ||
        '';
      // Flow + visitType branch on goalType. Backend handlers documented in
      // BACKEND_METABOLIC_FOLLOWUP_SUBMIT_PRD.md (metabolic) and
      // BACKEND_ANTIAGING_FOLLOWUP_ROUTES_PRD.md (antiAging — shipped add1893).
      const flow =
        goalType === 'antiAging'
          ? 'followup_antiaging'
          : goalType === 'metabolic'
          ? 'followup_metabolic'
          : goalType === 'trt'
          ? 'followup_trt'
          : goalType === 'womenSexualHealth'
          ? 'followup_womenSexualHealth'
          : goalType === 'mensSexualHealth'
          ? 'followup_mensSexualHealth'
          : goalType === 'brandedWeightloss'
          ? 'followup_brandedWeightloss'
          : (restartAtStarterDose ? 'followup_glp_simple' : 'followup_glp_full');
      const visitType = followupVisitType(goalType);

      // TRT followup ships extra envelope fields: lab integration is always-on
      // (Beluga requires fresh labs every 3 months — BACKEND_TRT_FOLLOWUP_PRD
      // §0.E), and the FE recomputes qADAM total so backend can verify.
      // labMethod defaults to the parent visit's lab_method when the patient
      // doesn't explicitly re-pick (FollowUp questionnaire has no labMethod
      // slide by design; provider can override post-submission).
      const trtExtras =
        goalType === 'trt'
          ? {
              belugaOrdersLabs: true,
              labMethod: userData?.labOrder?.labMethod || userData?.lab_method || 'atHome',
              scores: { qadamTotal: computeTrtFollowUpQadam(answers) },
            }
          : {};

      // Each follow-up is a separate paid visit — backend MUST mint a new
      // Beluga masterId rather than reusing the patient's prior one. The
      // `previousMasterId` is the linkage back to the source intake (and
      // any prior follow-ups), used by backend to prefill clinical history
      // and chain the visit thread. See BACKEND_FOLLOWUP_NEW_MASTERID_PRD.md
      // for the full contract.
      const previousMasterId = userData?.masterId || masterIdParam || null;

      // §branded-refill (§3/§7): quoteId + the quote's visitType (bound leg only),
      // rxId, fulfillmentRoute, pharmacySelection, pharmacy{choice}, dose{…}.
      // 409 quote_mismatch / 422 pharmacy_required / 422 invalid_dose auto-refund
      // and are relayed raw below like every other post-payment failure.
      const refillExtras = submitExtrasFrom(quote, answers, { rxId: refillParams.rxId, bound: quoteBound });

      const result = await apiService.submitFollowUp({
        email: userEmail,
        previousMasterId,
        answers: formattedAnswers,
        contact,
        paymentId,
        flow,
        visitType,
        patientPreference,
        restartAtStarterDose,
        paymentResult,
        ...trtExtras,
        ...refillExtras,
      });

      // Chain forward — backend minted a NEW masterId for this followup.
      // If the patient submits another followup in the same session (rare
      // but possible from the dashboard wrapper), its previousMasterId
      // must reference THIS visit, not the original intake. Mutating
      // userData in place is the lightest fix; a full refetch from
      // /followup/lookup-user is unnecessary since the rest of the
      // returning userData is unchanged. See FE_PRD_FOLLOWUP_VISIT_DETAILS.md
      // §3 for the rationale.
      if (result?.masterId && userData) {
        setUserData({
          ...userData,
          masterId: result.masterId,
          previousMasterId: result.previousMasterId,
          // The changed address is now what this visit (the next parent) holds.
          ...(shipTo.changed || {}),
        });
      }
      shipTo.afterSubmit();

      setSubmissionId(result.submissionId);
      setVisitResult(result);
      // §embed-bridge: tell the dashboard what was created. Waits for any
      // in-flight photo relay first — the parent closes its modal on this
      // message, which unmounts the iframe and would abort the upload.
      emitVisitCompleteWhenPhotosSettle({
        masterId: result?.masterId,
        visitId: result?.visitId,
        scheduleLink: result?.scheduleLink,
        ...(quote ? { previousMasterId, ...visitCompleteExtrasFrom(quote, answers) } : {}),
      });
      setIsComplete(true);
      setSlideDirection('forward');
      setSlideKey(prev => prev + 1);
    } catch (error) {
      // §branded-refill: the visit already exists for this quote (a double
      // submit). No refund, nothing to fix — show the success state and tell the
      // dashboard, same as a first success.
      if (isQuoteAlreadySubmittedError(error)) {
        const d = error?.response?.data || {};
        emitVisitCompleteWhenPhotosSettle({
          masterId: d.masterId,
          visitId: d.visitId,
          ...(quote ? { previousMasterId: userData?.masterId || masterIdParam || null, ...visitCompleteExtrasFrom(quote, answers) } : {}),
        });
        setIsComplete(true);
        setSlideDirection('forward');
        setSlideKey(prev => prev + 1);
        return;
      }
      console.error('Submission error:', error);
      // §embed-bridge: relay the RAW backend error — the dashboard reads
      // errorCode/refundId to tell the patient their card was refunded.
      emitVisitError(error);
      alert(describeSubmitFailure(error));
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
      setLoading(false);
    }
  }, [answers, userData, userEmail, currentFlow, goalType, masterIdParam, handleDisqualify, shipTo, quote, quoteBound, refillParams.rxId]);

  // Start over
  const handleStartOver = useCallback(() => {
    setSlideDirection('backward');
    setSlideKey(prev => prev + 1);
    setCurrentStep(0);
    setAnswers({});
    setIsComplete(false);
    setSubmissionId(null);
    setIsDisqualified(false);
    setDisqualifyReason('');
    setIsUserVerified(false);
    setUserData(null);
    setUserEmail('');
    shipTo.reset();
    refillQuote.reset();
    setStaleNotice('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipTo, refillQuote.reset]);
  
  // Get animation class
  const getAnimationClass = () => {
    switch (slideDirection) {
      case 'forward':
        return 'slide-forward';
      case 'backward':
        return 'slide-backward';
      default:
        return 'slide-initial';
    }
  };
  
  // §branded-refill: seed the pharmacy search from the address on file (the
  // lookup shape isn't pinned for address fields — read the likely spellings).
  const refillSearchDefaults = useMemo(() => {
    const a = shipToFromLookup(userData) || {};
    return {
      city: a.city || userData?.city || userData?.user_city || '',
      state: a.state || userData?.state || userData?.user_state || '',
      zip: a.zip || userData?.zip || userData?.user_zip || '',
    };
  }, [userData]);
  // Staging sign-off bypass for the quote checkout — same gate as CheckoutStep.
  const stagingBypass =
    typeof window !== 'undefined' &&
    goalType === 'brandedWeightloss' &&
    new URLSearchParams(window.location.search).get('belugaEnv') === 'staging';

  // Get user's display name
  const getUserDisplayName = () => {
    if (userData?.firstName) {
      return userData.firstName;
    }
    return 'Patient';
  };
  
  // Render step
  const renderStep = () => {
    // Show email lookup if not verified
    if (!isUserVerified) {
      return (
        <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
          <FollowUpEmailStep
            email={userEmail}
            setEmail={setUserEmail}
            onLookup={handleUserLookup}
            isLoading={isLookingUp}
            error={lookupError}
          />
        </div>
      );
    }
    
    // §branded-refill: hold the first slide while the initial quote is in
    // flight — it decides which questionnaire runs (capped by the hook's
    // timeout, after which today's flow renders).
    if (refillQuote.status === 'loading') {
      return (
        <div key="quote-pending" className="slide-wrapper slide-initial" data-testid="followup-quote-pending">
          <p className="text-center text-gray-500 py-8">Preparing your refill…</p>
        </div>
      );
    }

    // Show disqualified screen
    if (isDisqualified) {
      return (
        <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
          <DisqualifiedStep 
            reason={disqualifyReason} 
            onStartOver={handleStartOver}
          />
        </div>
      );
    }
    
    // Show success screen
    if (isComplete) {
      return (
        <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
          <VideoVisitNote visitResult={visitResult} className="max-w-xl mx-auto mt-6 mb-2" />
          <FollowUpSuccessStep 
            submissionId={submissionId}
            userName={getUserDisplayName()}
            onStartOver={handleStartOver}
          />
        </div>
      );
    }
    
    // Show current question
    if (!currentQuestion) return null;
    
    const commonProps = {
      question: currentQuestion,
      value: answers[currentQuestion.id],
      onAnswer: (answer) => handleAnswer(currentQuestion.id, answer),
      onNext: handleNext,
      onBack: handleBack,
      canGoBack: currentStep > 0,
      isLastStep: currentStep === totalSteps - 1,
      userName: getUserDisplayName(),
      userData: userData,
      setLoading: setLoading
    };
    
    const stepContent = (() => {
      switch (currentQuestion.type) {
        case 'textarea-prefilled':
          return (
            <PrefilledTextareaStep
              {...commonProps}
              prefilledValue={userData?.[currentQuestion.fieldMapping] || ''}
            />
          );
        case 'single-select-prefilled':
          return (
            <PrefilledQuestionStep
              {...commonProps}
              value={answers[currentQuestion.id] || userData?.[currentQuestion.fieldMapping]}
            />
          );
        case 'single-select':
          return <QuestionStep {...commonProps} />;
        case 'multi-select':
          return <MultiSelectStep {...commonProps} />;
        case 'multi-select-with-addons':
          return <MultiSelectWithAddonsStep {...commonProps} />;
        case 'textarea':
          return <TextareaStep {...commonProps} />;
        case 'textarea-with-upload':
          return <TextareaWithUploadStep {...commonProps} />;
        case 'text':
          return <InputStep {...commonProps} />;
        case 'height-weight-bmi':
          return (
            <HeightWeightBmiStep
              {...commonProps}
              onDisqualify={handleDisqualify}
              isOnTreatment={true}
            />
          );
        case 'consent-dropdown':
          return (
            <ConsentDropdownStep
              {...commonProps}
              onDisqualify={handleDisqualify}
            />
          );
        case 'info-text':
          return <InfoTextStep {...commonProps} />;
        case 'medication-selection':
          return <MedicationSelectionStep {...commonProps} />;
        case 'refill-pharmacy':
          return (
            <RefillPharmacyStep
              {...commonProps}
              quote={quote}
              searchDefaults={refillSearchDefaults}
            />
          );
        case 'refill-dose':
          return <RefillDoseStep {...commonProps} quote={quote} previewQuote={refillQuote.previewQuote} />;
        case 'checkout':
          if (quoteBound) {
            return (
              <RefillQuoteCheckout
                quote={quote}
                answers={answers}
                onComplete={handleCheckoutComplete}
                isProcessing={isSubmitting}
                setLoading={setLoading}
                email={userEmail}
                currentStep={currentStep}
                timestamp={Date.now()}
                paymentHeld={shipTo.editing}
                onQuoteStale={handleQuoteStale}
                staleNotice={staleNotice}
                requoting={refillQuote.requoting}
                stagingBypass={stagingBypass}
                modalityState={refillSearchDefaults.state}
                shipTo={
                  <ShipToCard
                    onFile={shipTo.onFile}
                    changed={shipTo.changed}
                    saved={shipTo.saved}
                    emptyText={shipTo.mode === 'auth' ? 'No address on file.' : undefined}
                    addLabel={shipTo.mode === 'auth' ? 'Add address' : undefined}
                    onChange={shipTo.save}
                    onEditingChange={shipTo.setEditing}
                  />
                }
              />
            );
          }
          return (
            <CheckoutStep
              onComplete={handleCheckoutComplete}
              isProcessing={isSubmitting}
              answers={answers}
              setLoading={setLoading}
              clearSavedProgress={() => {}}
              primaryGoal="followup"
              modalityVisitType={followupVisitType(goalType)}
              modalityState={refillSearchDefaults.state}
              currentStep={currentStep}
              timestamp={Date.now()}
              email={userEmail}
              goalType={goalType}
              userData={userData}
              paymentHeld={shipTo.editing}
              shipTo={
                <ShipToCard
                  onFile={shipTo.onFile}
                  changed={shipTo.changed}
                  saved={shipTo.saved}
                  emptyText={shipTo.mode === 'auth' ? 'No address on file.' : undefined}
                  addLabel={shipTo.mode === 'auth' ? 'Add address' : undefined}
                  onChange={shipTo.save}
                  onEditingChange={shipTo.setEditing}
                  validateState={
                    goalType === 'trt'
                      ? (code) => (isTrtBlockedState(code) ? TRT_BLOCKED_STATE_MESSAGE : '')
                      : undefined
                  }
                />
              }
            />
          );
        default:
          return <QuestionStep {...commonProps} />;
      }
    })();
    
    return (
      <div key={slideKey} className={`slide-wrapper ${getAnimationClass()}`}>
        {stepContent}
      </div>
    );
  };

  // §0.A — Render nothing for direct traffic; the redirect useEffect above
  // is already taking the user to dashboard login. This keeps the form
  // chrome from briefly flashing before the navigation lands.
  if (!isEmbed) return null;

  return (
    <>
      <div className='intake-form-loader' style={{ display: loading ? '' : 'none' }}>
        <MutatingDots />
      </div>
      
      <div className="min-h-screen flex flex-col bg-white" data-testid="followup-form" style={{ display: loading === false ? '' : 'none' }}>
        {/* EMS warning modal — TRT followup acute-symptoms=Yes */}
        {emsWarning && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            data-testid="ems-warning-modal"
          >
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Seek immediate medical care
                </h3>
                <p className="text-gray-600">{emsWarning.message}</p>
              </div>
              <div className="space-y-3">
                <a
                  href="tel:911"
                  className="block w-full py-3 px-4 bg-red-600 text-white text-center font-medium rounded-xl hover:bg-red-700 transition-colors"
                  data-testid="ems-call-911-btn"
                >
                  Call 911
                </a>
                <button
                  onClick={handleEmsContinue}
                  className="w-full py-3 px-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
                  data-testid="ems-continue-btn"
                >
                  Continue with my visit anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <header className="w-full py-4 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            {/* Back button */}
            {isUserVerified && currentStep > 0 && !isComplete && !isDisqualified ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
                data-testid="back-button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">Back</span>
              </button>
            ) : (
              <div className="w-16 flex-shrink-0" />
            )}
            
            {/* Logo */}
            <div className="text-center flex-1">
              <BrandLogo
                alt="FitPal"
                className="mx-auto"
                style={{ width: '400px', height: 'auto', maxWidth: '100%' }}
              />
              {isUserVerified && !isComplete && !isDisqualified && (
                <p className="text-xs text-gray-500 mt-1">
                  {goalType === 'antiAging'
                    ? 'Anti-Aging Follow-Up Questionnaire'
                    : goalType === 'metabolic'
                    ? 'Metabolic Support Follow-Up Questionnaire'
                    : goalType === 'trt'
                    ? 'TRT Follow-Up Questionnaire'
                    : goalType === 'womenSexualHealth'
                    ? "Women's Sexual Health Follow-Up Questionnaire"
                    : goalType === 'mensSexualHealth'
                    ? "Men's Sexual Health Follow-Up Questionnaire"
                    : goalType === 'brandedWeightloss'
                    ? 'Weight Loss (Branded GLP-1) Follow-Up Questionnaire'
                    : 'Weight Loss Follow-Up Questionnaire'}
                </p>
              )}
            </div>
            
            <div className="w-16 flex-shrink-0" />
          </div>
        </header>
        
        {/* User greeting */}
        {isUserVerified && userData && !isComplete && !isDisqualified && (
          <div className="w-full px-4 py-2">
            <div className="max-w-2xl mx-auto text-center">
              <p className="text-sm text-[#2d8a5f] font-medium">
                Welcome back, {getUserDisplayName()}!
              </p>
            </div>
          </div>
        )}
        
        {/* Progress Bar */}
        {isUserVerified && !isComplete && !isDisqualified && totalSteps > 0 && (
          <div className="w-full px-4 py-2">
            <div className="max-w-2xl mx-auto">
              <ProgressBar current={currentStep} total={totalSteps} />
            </div>
          </div>
        )}
        
        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center justify-start px-4 py-8">
          <div className="w-full max-w-xl slide-container">
            {renderStep()}
          </div>
        </main>
        
        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default FollowUpForm;
